import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  RemoveAlertDialog,
} from '@wegroup/design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { InsurancePolicyPackage } from '../../../../../../types/AdvisoryReport';
import { omit } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { PackageFormValues, PackageSchema } from '../../../../types/Form';
import PackageEditModalFooter from './PackageEditModalFooter';
import PackageEditModalBody from './PackageEditModalBody';
import { usePutInsurancePackage } from '../../../../api/packages/putInsurancePackage';
import { format, isValid } from 'date-fns';

interface Props {
  reportId?: string;
  initialData: InsurancePolicyPackage;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (policyPackage: InsurancePolicyPackage) => void;
}

const PackageEditModal: React.FC<Props> = ({
  reportId,
  onClose,
  isOpen,
  onEdit,
  initialData,
}) => {
  const { t } = useTranslation();
  const closeAlert = useDisclosure();
  const defaultValues = omit(
    {
      ...initialData,
      payment_interval: initialData.payment_interval || 'ANNUAL',
      due_date:
        initialData.due_date && isValid(new Date(initialData.due_date))
          ? format(new Date(initialData.due_date), 'dd/MM')
          : undefined,
    },
    'insurance_policies',
  );
  const { mutate: putInsurancePackage } = usePutInsurancePackage();
  const methods = useForm<PackageFormValues>({
    defaultValues,
    resolver: yupResolver(PackageSchema),
  });

  const handleClose = () => {
    methods.reset(defaultValues);
    onClose();
  };

  const handleSubmit = methods.handleSubmit((values) => {
    if (reportId) {
      putInsurancePackage({
        reportId,
        insurancePackage: {
          ...initialData,
          ...values,
          due_date: values.due_date
            ? format(new Date(values.due_date), 'MM-dd')
            : null,
          insurance_policies: initialData.insurance_policies,
        },
      });
    }
    onEdit?.({
      ...initialData,
      ...values,
      due_date: values.due_date
        ? format(new Date(values.due_date), 'MM-dd')
        : null,
      insurance_policies: initialData.insurance_policies,
    });
    methods.reset({ ...initialData, ...values });
    onClose();
  });

  return (
    <>
      <RemoveAlertDialog
        onClose={closeAlert.onClose}
        isOpen={closeAlert.isOpen}
        onConfirm={handleClose}
        title={t('ARE-YOU-SURE')}
        description={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ARE-YOU-SURE',
        )}
        confirmButtonText={t('CLOSE')}
      />
      <Modal
        isOpen={isOpen}
        onClose={methods.formState.isDirty ? closeAlert.onOpen : handleClose}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`ADVISORY-REPORTS_CREATION-FLOW_PACKAGE_MODAL_TITLE`)}
          </ModalHeader>
          <ModalCloseButton />
          <FormProvider {...methods}>
            <PackageEditModalBody />
          </FormProvider>
          <PackageEditModalFooter
            onSubmit={handleSubmit}
            onClose={
              methods.formState.isDirty ? closeAlert.onOpen : handleClose
            }
            isDisabled={!methods.formState.isDirty}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

export default PackageEditModal;
